import React, { useEffect, useState } from "react";
import { Plus, Pencil, Power } from "lucide-react";
import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "../../../components/ui/TableV2";
import Breadcrumb from "../../../components/ui/Breadcrumb";
import { Badge } from "../../../components/ui/Badge";
import AddSupplierModal from "../../../components/modals/AddSupplierModal";
import { formatDateTime } from "src/utils/date-time";
import { formatCreatedBy } from "src/utils/common";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as supplierActions from "src/redux/slices/supplierSlice";
import { toast } from "react-toastify";
import PageHeader from "src/components/common/PageHeader";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Card from "src/components/common/card/Card";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import { Link } from "react-router-dom";

const SupplierPage: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [limit] = useState(10);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<any | null>(null);
  const [statusUpdate, setStatusUpdate] = useState(false);

  const { supplierList, editSupplier } = useAppSelector(
    (state) => state.supplier
  );
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreateSupplier =
    isTenantAdmin || (permissions?.suppliers || []).includes("create");
  const canEditSupplier =
    isTenantAdmin || (permissions?.suppliers || []).includes("edit");
  const canToggleSupplier =
    isTenantAdmin || (permissions?.suppliers || []).includes("delete");
  const dispatch = useAppDispatch();

  useEffect(() => {
    getSuppliers(1);
  }, []);

  // ✅ Success/Error side-effects
  useEffect(() => {
    if (editSupplier.data && statusUpdate) {
      toast.success("Supplier modified successfully");
      dispatch(supplierActions.resetEditSupplier());
      setStatusUpdate(false);
      handleStatusModalClose();
      getSuppliers(1);
    } else if (editSupplier.error && statusUpdate) {
      toast.error(`Error: ${editSupplier.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
  }, [editSupplier, statusUpdate, dispatch]);

  const getSuppliers = (page: number) =>
    dispatch(
      supplierActions.getSupplierListRequest({
        page,
        limit,
      })
    );

  const handleModalClose = () => {
    setOpenModal(false);
    setSelectedRow(null);
  };

  const handleAddSupplierSuccess = () => {
    setOpenModal(false);
    getSuppliers(1);
  };

  const handleEdit = (data: any) => {
    const newData = {
      ...data,
      password: "",
    };
    setSelectedRow(newData);
    setOpenModal(true);
  };

  const handleStatusUpdate = (_id: string, status: "active" | "inactive") => {
    const payload = {
      _id: _id,
      status: status,
    };
    dispatch(supplierActions.editSupplierRequest(payload));
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    handleStatusUpdate(
      statusRow._id,
      statusRow.status === "active" ? "inactive" : "active"
    );
  };

  return (
    <DashboardLayout
      meta={{
        title: "Suppliers | Invyntra",
        description:
          "Manage and oversee all suppliers associated with your tenant.",
        keywords: "suppliers, management, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <PageHeader
          title="Suppliers"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Suppliers", path: "/app/suppliers" },
          ]}
        />

        {/* Card */}
        <Card>
          <CardHeader
            title="Supplier List"
            description="Manage suppliers associated with your tenant"
            actions={
              canCreateSupplier ? (
                <Button onClick={() => setOpenModal(true)}>
                  <Plus size={16} className="mr-1" /> Create
                </Button>
              ) : null
            }
          />

          <TableV2
            columns={[
              {
                key: "supplierId",
                header: "Supplier ID",
                accessor: ({ row, key }) => (
                  <Link to={`/app/supplier/${row?._id}`} className="underline">
                    #{row[key]}
                  </Link>
                ),
              },
              { key: "name", header: "Supplier Name" },
              { key: "contactPerson", header: "Contact Person" },
              { key: "phone", header: "Phone" },
              { key: "gstNumber", header: "GST Number" },
              { key: "email", header: "Email" },
              { key: "address", header: "Address", width: "250px" },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ row }) => (
                  <Badge
                    text={row.status}
                    color={row.status === "active" ? "green" : "red"}
                  />
                ),
              },
              {
                key: "createdAt",
                header: "Created On",
                accessor: ({ row }) => (
                  <div>{formatDateTime(row.createdAt, "DD/MM/YYYY")}</div>
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) => (
                  <div className="pr-2">
                    {canEditSupplier && (
                      <button
                        className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                        onClick={() => handleEdit(row)}
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {canToggleSupplier && (
                      <button
                        className={`p-2 rounded-md ${
                          row.status === "active"
                            ? "hover:bg-red-100 text-red-600"
                            : "hover:bg-green-100 text-green-600"
                        }`}
                        onClick={() => {
                          setStatusRow(row);
                          setShowStatusModal(true);
                        }}
                      >
                        <Power size={14} />
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            data={supplierList?.data?.data}
            pagination={supplierList?.data?.pagination}
            onPageChange={(page) => getSuppliers(page)}
          />
        </Card>

        {openModal && (
          <AddSupplierModal
            open={openModal}
            supplier={selectedRow}
            onClose={handleModalClose}
            onSuccess={handleAddSupplierSuccess}
          />
        )}
      </div>

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            statusRow.status === "active" ? "deactivate" : "activate"
          } this supplier?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editSupplier.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default SupplierPage;
